import type { BrandConfig } from "../../config/types.ts";
import { normalizeHostname } from "../shared/hostname.ts";
import {
  brands,
  findBrandByDomain,
  PRIMARY_BRAND_ID,
  FALLBACK_BRAND_ID,
} from "../../config/brands.ts";

export { normalizeHostname } from "../shared/hostname.ts";

export interface BrandResolutionResult {
  brand: BrandConfig;
  source: "domain" | "env" | "explicit" | "client_window" | "fallback";
  matchedDomain?: string;
  isFallback: boolean;
}

/**
 * Resuelve la marca desde un hostname con trazabilidad (origen y fallback)
 */
export function resolveBrandByHostname(hostname?: string): BrandResolutionResult {
  const normalized = normalizeHostname(hostname);

  if (normalized) {
    const matchedBrand = findBrandByDomain(normalized);
    if (matchedBrand) {
      return {
        brand: matchedBrand,
        source: "domain",
        matchedDomain: normalized,
        isFallback: false,
      };
    }
  }

  const fallbackBrand = brands[FALLBACK_BRAND_ID] || brands[PRIMARY_BRAND_ID];
  return {
    brand: fallbackBrand,
    source: "fallback",
    matchedDomain: normalized || undefined,
    isFallback: true,
  };
}

/**
 * Resuelve la marca activa: dominio primero, luego variables de entorno, luego reserva
 */
export function getActiveBrand(options?: { hostname?: string; brandId?: string }): BrandConfig {
  return getActiveBrandResolution(options).brand;
}

export function getActiveBrandResolution(options?: {
  hostname?: string;
  brandId?: string;
}): BrandResolutionResult {
  // Marca explícita por opciones
  if (options?.brandId && brands[options.brandId]) {
    return {
      brand: brands[options.brandId],
      source: "explicit",
      isFallback: false,
    };
  }

  // Hostname explícito por opciones
  if (options?.hostname) {
    const res = resolveBrandByHostname(options.hostname);
    if (!res.isFallback) return res;
  }

  // Dominio desde entorno (p. ej. DOCS_DOMAIN=docs.zorya.com)
  const envDomain =
    typeof process !== "undefined"
      ? process.env.DOCS_DOMAIN || process.env.VITE_DOCS_DOMAIN || process.env.DOMAIN
      : undefined;

  if (envDomain) {
    const res = resolveBrandByHostname(envDomain);
    if (!res.isFallback) return res;
  }

  // Marca explícita por entorno (p. ej. BRAND=zorya)
  const envBrandId =
    typeof process !== "undefined"
      ? process.env.BRAND || process.env.VITE_BRAND
      : undefined;

  if (envBrandId && brands[envBrandId]) {
    return {
      brand: brands[envBrandId],
      source: "env",
      isFallback: false,
    };
  }

  const primaryBrand = brands[PRIMARY_BRAND_ID] || brands[FALLBACK_BRAND_ID];
  return {
    brand: primaryBrand,
    source: "fallback",
    isFallback: true,
  };
}